/**
 * Registro manual de entrada.
 *
 * Para vehículos que la cámara no alcanzó a reconocer: el operador escribe la
 * placa y el tipo, y el sistema crea la entrada como si la hubiera leído la
 * cámara. Queda registrado quién lo hizo, porque salta ese control.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogActions, Box, Typography, TextField,
  Button, Alert, CircularProgress, FormControl, InputLabel, Select,
  MenuItem, FormControlLabel, Checkbox, Divider,
} from "@mui/material";
import { DirectionsCar, Close as CloseIcon } from "@mui/icons-material";
import apiClient from "../../../utils/axios";

const PRIMARY = "#282626";
const ORANGE = "#F59E0B";

const FORM_VACIO = {
  numero_placa: "",
  sin_placa: false,
  tipo_vehiculo: "",
  color_vehiculo: "",
  numero_calza: "",
  observaciones: "",
  ajustar_hora: false,
  fecha_entrada: "",
};

// Formato que espera <input type="datetime-local">
const ahoraLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export default function EntradaManualDialog({ open, onClose, onRegistrado }) {
  const [form, setForm] = useState(FORM_VACIO);
  const [categorias, setCategorias] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [confirmacion, setConfirmacion] = useState("");
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...FORM_VACIO, fecha_entrada: ahoraLocal() });
    setError(""); setConfirmacion(""); setResultado(null);

    apiClient
      .get("/api/configuracion/grupos-tarifa-vehiculo/categorias")
      .then((res) => setCategorias((res.data?.data || []).filter((c) => c.asignable)))
      .catch(() => setError("No se pudo cargar el catálogo de tipos de vehículo"));
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const guardar = async (forzar = false) => {
    if (!form.sin_placa && !form.numero_placa.trim()) {
      setError("Ingresa la placa, la cédula, o marca 'sin placa legible'"); return;
    }
    if (!form.tipo_vehiculo) {
      setError("Selecciona el tipo de vehículo"); return;
    }
    setGuardando(true); setError("");
    try {
      const payload = {
        numero_placa: form.numero_placa.trim().toUpperCase(),
        sin_placa: form.sin_placa,
        tipo_vehiculo: form.tipo_vehiculo,
        color_vehiculo: form.color_vehiculo.trim(),
        numero_calza: form.numero_calza.trim(),
        observaciones: form.observaciones.trim(),
        forzar,
      };
      // Solo se envía si el operador la ajustó; si no, el backend usa "ahora"
      if (form.ajustar_hora && form.fecha_entrada) {
        payload.fecha_entrada = form.fecha_entrada;
      }
      const res = await apiClient.post("/api/vehiculos/entrada-manual", payload);
      setResultado(res.data?.data || null);
      setConfirmacion("");
      onRegistrado?.();
    } catch (e) {
      const data = e.response?.data;
      if (data?.requiere_confirmacion) {
        // Ya tiene una entrada abierta reciente: se pide confirmar en vez de fallar
        setConfirmacion(data.error);
      } else {
        setError(data?.error || "No se pudo registrar la entrada");
      }
    } finally {
      setGuardando(false);
    }
  };

  const inp = { size: "small", fullWidth: true, sx: { "& .MuiOutlinedInput-root": { borderRadius: 2 } } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <Box sx={{ bgcolor: PRIMARY, px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <DirectionsCar sx={{ color: ORANGE }} />
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700} sx={{ color: "white" }}>Registro manual de entrada</Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
            Para vehículos que la cámara no reconoció
          </Typography>
        </Box>
      </Box>

      {resultado ? (
        <>
          <DialogContent sx={{ px: 3, pt: 3 }}>
            <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
              Entrada registrada correctamente
            </Alert>
            <Box sx={{ bgcolor: "#f8f8f8", borderRadius: 2, p: 2 }}>
              {[
                ["Placa", resultado.sin_placa
                  ? `${resultado.numero_placa} (sin placa)`
                  : resultado.es_cedula
                    ? `${resultado.numero_placa} (cédula)`
                    : resultado.numero_placa],
                ["Tipo", resultado.tipo_vehiculo_label || resultado.tipo_vehiculo],
                ...(resultado.numero_calza ? [["N° de calza", resultado.numero_calza]] : []),
                ["Código QR", resultado.codigo_qr],
              ].map(([k, v]) => (
                <Box key={k} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: "#888" }}>{k}</Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ color: PRIMARY }}>{v}</Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: "#888", display: "block", mt: 1.5 }}>
              El vehículo ya aparece en la bitácora y se le puede cobrar normalmente desde su fila.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button variant="contained" onClick={onClose} fullWidth
              sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: "white", "&:hover": { bgcolor: "#D97706" } }}>
              Listo
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent sx={{ px: 3, pt: 3, display: "flex", flexDirection: "column", gap: 1.75 }}>
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            {confirmacion && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>{confirmacion}</Alert>
            )}

            <TextField label="Placa o cédula" value={form.numero_placa}
              onChange={(e) => set("numero_placa", e.target.value.toUpperCase())}
              disabled={form.sin_placa} placeholder="PBZ1754 o 0912345678"
              helperText="Si el vehículo no tiene placa legible, se puede identificar por la cédula del conductor"
              {...inp} />

            <FormControlLabel
              sx={{ mt: -1 }}
              control={<Checkbox size="small" checked={form.sin_placa}
                onChange={(e) => set("sin_placa", e.target.checked)} />}
              label={<Typography variant="caption">Sin placa legible</Typography>}
            />

            <FormControl {...inp}>
              <InputLabel>Tipo de vehículo *</InputLabel>
              <Select label="Tipo de vehículo *" value={form.tipo_vehiculo}
                onChange={(e) => set("tipo_vehiculo", e.target.value)}>
                {categorias.map((c) => (
                  <MenuItem key={c.slug} value={c.slug}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ color: "#888", mt: -1.25 }}>
              Define la tarifa que se le aplicará al cobrar.
            </Typography>

            <TextField label="Color (opcional)" value={form.color_vehiculo}
              onChange={(e) => set("color_vehiculo", e.target.value)} {...inp} />

            <TextField label="N° de calza (opcional)" value={form.numero_calza}
              onChange={(e) => set("numero_calza", e.target.value)}
              inputProps={{ maxLength: 10 }}
              helperText="Puesto físico asignado (ej. servicios de baño con calzas numeradas)"
              {...inp} />

            <Divider />

            <FormControlLabel
              control={<Checkbox size="small" checked={form.ajustar_hora}
                onChange={(e) => set("ajustar_hora", e.target.checked)} />}
              label={<Typography variant="caption">El vehículo entró antes de ahora</Typography>}
            />
            {form.ajustar_hora && (
              <>
                <TextField label="Hora real de entrada" type="datetime-local"
                  value={form.fecha_entrada}
                  onChange={(e) => set("fecha_entrada", e.target.value)}
                  InputLabelProps={{ shrink: true }} {...inp} />
                <Typography variant="caption" sx={{ color: "#888", mt: -1.25 }}>
                  El cobro se calculará desde esta hora, no desde el momento del registro.
                </Typography>
              </>
            )}

            <TextField label="Observaciones (opcional)" value={form.observaciones}
              onChange={(e) => set("observaciones", e.target.value)}
              multiline rows={2} {...inp} />
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={onClose} startIcon={<CloseIcon />} sx={{ color: "#888", borderRadius: 2 }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={() => guardar(!!confirmacion)} disabled={guardando}
              startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, color: "white", "&:hover": { bgcolor: "#D97706" }, minWidth: 150 }}>
              {guardando ? "Registrando…" : confirmacion ? "Registrar de todos modos" : "Registrar entrada"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
